/** Same output as nuxt-seo-utils' default "%s %separator %siteName". */
export const SITE_NAME_SUFFIX = "%separator %siteName";

const SITE_NAME_TOKEN = "%sitename";

/** A spaced "|", "-", "–", "—", "·" or ":" — "Crew-Neck" is not split. */
const TITLE_SEGMENT_SEPARATOR = /\s+[|\-–—·:]\s+/;

/** "Bread & Boxers", "Bread and Boxers" and "Bread&Boxers" → "breadandboxers". */
const normalizeBrand = (value: string): string =>
	value
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^\p{L}\p{N}%]+/gu, "");

/**
 * Title template that skips the site-name suffix when the title already names
 * the site as its own leading or trailing segment ("Herr | Bread & Boxers",
 * "Bread & Boxers - Din basgarderob") — Storyblok SEO titles often carry the
 * brand. A brand inside a product name ("Bread & Boxers Gift Card") still
 * gets the suffix.
 */
export const buildPageTitle = (
	title: string | null | undefined,
	siteName: string | undefined
): string => {
	const trimmed = title?.trim();
	if (!trimmed) return "%siteName";

	const brand = siteName ? normalizeBrand(siteName) : "";
	const segments = trimmed.split(TITLE_SEGMENT_SEPARATOR);
	const edges = [segments[0], segments[segments.length - 1]].map((segment) =>
		normalizeBrand(segment ?? "")
	);
	const namesSite = edges.some(
		(edge) => edge === SITE_NAME_TOKEN || (!!brand && edge === brand)
	);

	return namesSite ? trimmed : `${trimmed} ${SITE_NAME_SUFFIX}`;
};

/**
 * The title without a leading or trailing site-name segment — the inverse of
 * `buildPageTitle`, for surfaces that already show the brand (an OG card with
 * the logo). A title that is only the site name is kept as it is.
 */
export const stripSiteName = (
	title: string,
	siteName: string | undefined
): string => {
	const brand = siteName ? normalizeBrand(siteName) : "";
	if (!brand) return title;

	const separators = [
		...title.matchAll(new RegExp(TITLE_SEGMENT_SEPARATOR, "g"))
	];
	if (!separators.length) return title;

	const first = separators[0]!;
	const last = separators[separators.length - 1]!;
	let start = 0;
	let end = title.length;
	if (normalizeBrand(title.slice(last.index! + last[0].length)) === brand) {
		end = last.index!;
	}
	if (normalizeBrand(title.slice(0, first.index)) === brand) {
		start = first.index! + first[0].length;
	}
	const rest = start < end ? title.slice(start, end).trim() : "";
	return rest || title;
};
